import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import { downloadAuthenticatedFile } from "@/lib/download-blob";
import type { Submission, ListSubmissionsQuery } from "@/types/submission";
import type { PaginatedResult } from "@/types/common";

interface SubmissionPagination {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const EMPTY_META = { total: 0, page: 1, limit: 25, totalPages: 1 };

export const submissionService = {
  async list(query?: ListSubmissionsQuery): Promise<PaginatedResult<Submission>> {
    const response = await http.get(API_ENDPOINTS.SUBMISSIONS.LIST, {
      // The backend reads `hasAttachment` as the literal string "true"; omit it entirely when off.
      params: { ...query, hasAttachment: query?.hasAttachment ? "true" : undefined },
    });
    const data = unwrapData<{
      items?: Submission[];
      pagination?: SubmissionPagination;
    }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    const pagination = data?.pagination;
    return {
      items,
      meta: pagination
        ? {
            total: pagination.total,
            page: pagination.page,
            limit: pagination.pageSize,
            totalPages: pagination.pageCount,
          }
        : EMPTY_META,
    };
  },

  async get(id: string): Promise<Submission> {
    const response = await http.get(API_ENDPOINTS.SUBMISSIONS.DETAIL(id));
    return unwrapData<Submission>(response);
  },

  /**
   * CR-001 FR-1.8 — saves the visitor's file under its ORIGINAL name (taken from the response's
   * Content-Disposition; `fallbackName` only covers a header the server failed to send).
   * Goes through the authenticated client so FR-1.9 holds: no token, no file.
   */
  async downloadAttachment(id: string, fallbackName: string): Promise<void> {
    await downloadAuthenticatedFile(
      API_ENDPOINTS.SUBMISSIONS.ATTACHMENT(id),
      undefined,
      fallbackName,
    );
  },
};
